/**
 * User-facing copy for Scores list empty and error states.
 * Keeps technical details (API, HTTP, backend) out of the UI.
 */

export const SCORE_LIST_MESSAGES = {
  EMPTY_DATA: "We couldn't load scores right now. Please try again in a moment.",
  TIMEOUT: 'Loading scores is taking longer than expected. Try again or filter by a single course.',
  LOADING: 'Loading scores…',
};

/** Default fetch timeout for org-wide scores (ms). */
export const SCORES_FETCH_TIMEOUT_MS = 120000;

/**
 * Map a fetch failure to a friendly message without exposing infrastructure details.
 */
export function getScoreListFetchErrorMessage(err, { timedOut = false } = {}) {
  if (timedOut) {
    return SCORE_LIST_MESSAGES.TIMEOUT;
  }

  if (!err) {
    return SCORE_LIST_MESSAGES.EMPTY_DATA;
  }

  if (err.name === 'AbortError') {
    return SCORE_LIST_MESSAGES.TIMEOUT;
  }

  if (err.status === 401 || err.status === 403) {
    return 'Your session may have expired. Refresh the page and sign in again.';
  }

  if (err.status === 404) {
    return 'No scores are available yet. Results appear after learners complete graded assessments.';
  }

  if (err.status >= 500) {
    return 'Something went wrong on our end. Please try again in a moment.';
  }

  return SCORE_LIST_MESSAGES.EMPTY_DATA;
}

/**
 * Parse a scores list response body. Returns rows or throws with status for messaging.
 */
export async function parseScoresListResponse(response) {
  if (!response.ok) {
    const error = new Error('Scores fetch failed');
    error.status = response.status;
    throw error;
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    const error = new Error('Unexpected scores response shape');
    error.status = response.status;
    throw error;
  }

  return data;
}
